import _map from 'lodash/map';
import _every from 'lodash/every';
import _slice from 'lodash/slice';
import SvgStepCurrent from '@unifyapps/icons/colored/StepCurrent';
import SvgChevronRight from '@unifyapps/icons/outline/ChevronRight';
import SvgStepIncomplete from '@unifyapps/icons/outline/StepIncomplete';
import SvgStepComplete from '@unifyapps/icons/colored/StepComplete';
import { clsx } from 'clsx';
import { Pill } from '../../Pill';
import Stack from '../../../_components/Stack';
import { Button } from '../../Button';
import type { EmptyStateAsStepProps } from '../types';
import EmptyStateTitle from './EmptyStateTitle';
import EmptyStateDescription from './EmptyStateDescription';

interface CustomStepperProps {
  steps: EmptyStateAsStepProps['steps'];
}

const getStepIcon = ({
  allPreviousStepsCompleted,
  step,
  index,
  activeStep,
}: {
  allPreviousStepsCompleted: boolean;
  step: EmptyStateAsStepProps['steps'][0];
  index: number;
  activeStep: number;
}) => {
  if (index <= activeStep && step.isCompleted && allPreviousStepsCompleted) {
    return <SvgStepComplete className="h-4 w-4" />;
  }

  if (
    (index === activeStep && allPreviousStepsCompleted) ||
    (index < activeStep && allPreviousStepsCompleted && step.isOptional)
  ) {
    return <SvgStepCurrent className="h-4 w-4" />;
  }

  return (
    <SvgStepIncomplete
      className="h-4 w-4 [&>circle]:stroke-transparent"
      fill="var(--bg-disabled)"
      stroke="var(--border-disabled)"
    />
  );
};

function CustomStepper(props: CustomStepperProps) {
  const { steps } = props;
  const firstIncompleteStep = _map(steps, (step) => step.isCompleted ?? false).indexOf(false);
  const activeStep = firstIncompleteStep === -1 ? steps.length - 1 : firstIncompleteStep;

  return (
    <Stack className="py-xl border-tertiary w-full border-b">
      {_map(steps, (step, index) => {
        const hasMoreSteps = index !== steps.length - 1;
        const allPreviousStepsCompleted = _every(
          _slice(steps, 0, index),
          (prevStep) => prevStep.isCompleted || prevStep.isOptional,
        );
        const stepIcon = getStepIcon({ allPreviousStepsCompleted, step, index, activeStep });
        const isCurrentStepValid = index === activeStep && allPreviousStepsCompleted;
        const buttonProps = step.onClick
          ? { onClick: step.onClick }
          : { href: step.href, component: 'a' as React.ElementType };

        return (
          // Negative margin is given as the step items are overlapping in the design
          <Stack
            className={clsx('w-[27.5rem]', index && 'mt-[-2px]')}
            direction="row"
            key={`${step.title} ${index}`}
          >
            <Stack className="gap-x-md w-[25.75rem]" direction="row">
              <Stack className="gap-y-xxs" direction="column">
                <Stack className="pt-md">{stepIcon}</Stack>
                {hasMoreSteps ? (
                  <Stack alignItems="center" className="h-full" direction="column">
                    <Stack
                      className={clsx('rounded-xxs w-[2px] flex-1 border', {
                        'border-brand-solid': step.isCompleted,
                        'border-secondary': !step.isCompleted,
                      })}
                      direction="column"
                    />
                  </Stack>
                ) : null}
              </Stack>
              <Stack
                className={clsx('w-full', {
                  'pb-3xl': hasMoreSteps,
                })}
                direction="column"
              >
                <Stack className="pt-sm pb-md px-md w-full" direction="column">
                  <Stack className="gap-x-md" direction="row">
                    <EmptyStateTitle
                      color={isCurrentStepValid ? 'text-brand-secondary' : 'text-secondary'}
                      title={step.title}
                      variant="text-sm"
                    />
                    {step.isOptional ? <Pill label="Optional" size="xs" variant="soft" /> : null}
                  </Stack>
                  <EmptyStateDescription
                    color={isCurrentStepValid ? 'text-brand-tertiary' : 'text-tertiary'}
                    description={step.description}
                  />
                </Stack>
              </Stack>
            </Stack>
            {isCurrentStepValid ? (
              <Stack alignItems="center" direction="row" justifyContent="center">
                <Button color="neutral" size="sm" variant="ghost" {...buttonProps}>
                  <SvgChevronRight className="text-brand-tertiary h-4 w-4" />
                </Button>
              </Stack>
            ) : null}
          </Stack>
        );
      })}
    </Stack>
  );
}

export default CustomStepper;
